"use client";

const PatternRenderer = dynamic(() =>
  import("@ui/components/composers/MusicOrb/PatternRenderer").then(mod => ({ default: mod.PatternRenderer })),
);
import { AspectRatio } from "@ui/components/core/AspectRatio";
import Pause20 from "@ui/icons/20x20/pause-20.svg";
import Play20 from "@ui/icons/20x20/play-20.svg";
import { useInView } from "@ui/lib/useInView";
import { cn } from "@ui/lib/utils";
import * as m from "motion/react-m";
import dynamic from "next/dynamic";
import { useEffect, useRef, useState } from "react";

import type { MusicColorScheme } from "./colorScheme";

export default function CoverArt({
  audioElement,
  title,
  styles,
  onClick,
  colorScheme,
  className,
  hidePlayButton,
  songId,
  boxShadow = true,
}: {
  audioElement: HTMLAudioElement | null;
  className?: string;
  colorScheme: MusicColorScheme;
  hidePlayButton?: boolean;
  onClick: () => void;
  songId?: string;
  styles?: string[];
  title?: string;
  boxShadow?: boolean;
}) {
  const [isPlaying, setIsPlaying] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  const inView = useInView(ref, { once: true, threshold: 0.1 });

  // Track audio playing state
  useEffect(() => {
    if (!audioElement) return;

    const handlePlay = () => setIsPlaying(true);
    const handlePause = () => setIsPlaying(false);
    const handleEnded = () => setIsPlaying(false);

    audioElement.addEventListener("play", handlePlay);
    audioElement.addEventListener("pause", handlePause);
    audioElement.addEventListener("ended", handleEnded);

    // Set initial state
    setIsPlaying(!audioElement.paused);

    return () => {
      audioElement.removeEventListener("play", handlePlay);
      audioElement.removeEventListener("pause", handlePause);
      audioElement.removeEventListener("ended", handleEnded);
    };
  }, [audioElement, songId]);

  return (
    <AspectRatio
      className={cn("relative w-full cursor-pointer", className)}
      onClick={onClick}
      ratio='1x1'
      ref={ref}
      style={
        boxShadow
          ? {
              boxShadow: "0 24px 80px 0 rgba(0, 0, 0, 0.16)",
            }
          : undefined
      }
    >
      <div className='absolute inset-0'>
        {inView && <PatternRenderer audioElement={audioElement} color={colorScheme} />}
      </div>
      <div className='absolute inset-0 z-2 flex flex-col justify-between p-20 opacity-80'>
        {title && (
          <m.p animate={{ opacity: 1 }} className='tracking-normal f-subhead-02 text-inverse' initial={{ opacity: 0 }}>
            {title}
          </m.p>
        )}
        <div className='flex items-end justify-between'>
          {styles && (
            <ul className='mr-16 flex min-w-0 flex-1 flex-col'>
              {styles?.slice(0, 5).map((styleTag, index) => (
                <m.li
                  animate={{ opacity: 1 }}
                  className='f-paragraph-03 truncate capitalize text-inverse'
                  initial={{ opacity: 0 }}
                  key={styleTag}
                  transition={{
                    duration: 0.25,
                    delay: (index + 1) * 0.1,
                  }}
                >
                  {styleTag}
                </m.li>
              ))}
            </ul>
          )}
          {!hidePlayButton && (
            <m.div
              animate={{ opacity: 1 }}
              initial={{ opacity: 0 }}
              transition={{
                duration: 0.5,
                delay: 1.5,
              }}
            >
              <button
                aria-label={isPlaying ? "Pause" : "Play"}
                className='flex h-40 w-40 cursor-pointer items-center justify-center rounded-full border border-overlay-darker bg-overlay-light text-inverse backdrop-blur-md'
                type='button'
              >
                {isPlaying ? <Pause20 className='h-20 w-20' /> : <Play20 className='h-20 w-20' />}
              </button>
            </m.div>
          )}
        </div>
      </div>
    </AspectRatio>
  );
}
